import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Optional,
  Output,
  Self,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  NgControl,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatDatepickerInputEvent,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import {
  MatFormFieldAppearance,
  MatFormFieldModule,
} from '@angular/material/form-field';
import { AssetsService } from '@sc/data-access-assets';
import { ConfigurationService } from '@sc/data-access-configuration';
import { allowedDatePattern } from '@sc/data-access-models';
import { TranslatorService } from '@sc/data-access-translation';
import { dayjs, randomId } from '@sc/utils';
import { Subscription } from 'rxjs';
import { ErrorMessagesService } from '../../services';
import { UtilsValidators } from '../../validators';
import { LabelRequiredPipe } from '../../pipes';
import { ErrorComponent } from '../error';
import { MatInputModule } from '@angular/material/input';
import { IconComponent } from '../icon';
import { NgIf, AsyncPipe } from '@angular/common';

@Component({
  selector: 'sc-date-picker',
  styleUrls: ['./date-picker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <mat-label *ngIf="isOutlineLabel()" [id]="labelId" class="sc-design-text-4">
      <label
        [for]="labelId"
        [innerHTML]="
          label
            | labelRequired
              : hasRequiredValidator(ngControl?.control)
              : forceHighlightRequired
        "
      ></label>
    </mat-label>
    <mat-form-field
      *ngIf="customFormControl"
      class="form-field"
      [style.width.px]="width ?? null"
      [class.hasHint]="!!hint"
      [class.error]="isError()"
      [appearance]="appearance"
      [hideRequiredMarker]="true"
    >
      <mat-label
        *ngIf="label && appearance !== 'outline'"
        class="sc-design-text-4"
        [attr.for]="input"
        [innerHTML]="
          label
            | labelRequired
              : hasRequiredValidator(ngControl?.control)
              : forceHighlightRequired
        "
      ></mat-label>
      <mat-datepicker-toggle
        class="prefix-calendar-icon-toggle"
        (click)="picker.open()"
        matPrefix
        [for]="picker"
        [disableRipple]="true"
      >
        <sc-icon
          name="calendar_hour"
          specificName="calendar_date_picker"
          matDatepickerToggleIcon
        ></sc-icon>
      </mat-datepicker-toggle>
      <mat-datepicker-toggle
        class="suffix-calendar-icon-toggle"
        (click)="picker.open()"
        matSuffix
        [for]="picker"
        [disableRipple]="true"
      >
        <sc-icon
          name="calendar_hour"
          specificName="calendar_date_picker"
          matDatepickerToggleIcon
        ></sc-icon>
      </mat-datepicker-toggle>
      <input
        #input
        matInput
        [id]="labelId"
        [formControl]="customFormControl"
        [value]="value"
        [matDatepicker]="picker"
        [min]="minDate"
        [max]="maxDate"
        [attr.data-testid]="getDataTestId()"
        [attr.title]="getInputTitle() | async"
        [attr.aria-required]="
          hasRequiredValidator(ngControl?.control) ? true : undefined
        "
        [attr.aria-invalid]="isError() ? true : undefined"
        [aria-describedby]="getAriaDescribedby()"
        [attr.aria-labelledby]="isOutlineLabel() ? labelId : undefined"
        (dateChange)="onDateChange($event)"
        (focusin)="onFocusin()"
        (focusout)="onFocusout()"
      />
      <mat-hint [id]="hintId" class="sc-design-text-4" *ngIf="!!hint">
        <p>{{ hint }}</p>
      </mat-hint>
      <mat-datepicker
        #picker
        panelClass="sc-date-picker-calendar-overlay"
      ></mat-datepicker>
    </mat-form-field>
    <sc-error
      *ngIf="displayError()"
      [id]="errorId"
      [class.sc-conf-error-on-input-focus]="focus"
      [error]="getError()"
      [displayCondition]="displayError()"
    ></sc-error>
  `,
  imports: [
    NgIf,
    MatFormFieldModule,
    MatDatepickerModule,
    IconComponent,
    MatInputModule,
    ReactiveFormsModule,
    ErrorComponent,
    AsyncPipe,
    LabelRequiredPipe,
  ],
})
export class DatePickerComponent
  implements OnInit, ControlValueAccessor, AfterViewInit
{
  @ViewChild('input') input: ElementRef<HTMLInputElement>;

  @Input() appearance: MatFormFieldAppearance = 'fill';
  @Input() width: number;
  @Input() isRequired: boolean;
  @Input() label: string;
  @Input() minDate: Date;
  @Input() maxDate: Date;
  @Input() hint: string;
  @Input() forceHighlightRequired: boolean;
  @Input() ariaDescribedby = '';
  @Input() dataTestId?: string;

  @Input() set disabled(newDisabled: boolean) {
    this.setDisabled(newDisabled);

    if (newDisabled) {
      this.ngControl?.control?.disable();
    } else {
      this.ngControl?.control?.enable();
    }
  }
  get disabled(): boolean {
    return this._disabled;
  }
  private _disabled = false;

  @Output() changeValue = new EventEmitter<Date>();
  customFormControl: FormControl;

  focus: boolean;
  valueChangesSubscription: Subscription;
  value: Date;
  errorId: string;
  hintId: string;
  labelId: string;

  onChange: any = () => {};
  onTouched: any = () => {};
  onKeyPress: any = () => true;

  constructor(
    @Self()
    @Optional()
    public ngControl: NgControl,
    private readonly changeDetectorRef: ChangeDetectorRef,
    public assetsService: AssetsService,
    public translateService: TranslatorService,
    private readonly errorMessageService: ErrorMessagesService,
    private readonly configService: ConfigurationService
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    this.appearance =
      this.configService.getConfig()?.themeOptions?.matInputAppearance ||
      'fill';

    this.hintId = randomId(8);
    this.errorId = randomId(8);
    this.labelId = 'date-picker-' + randomId(8);
  }

  ngAfterViewInit() {
    this.customFormControl = this.getFormControl();
    this.hint =
      this.hint ??
      this.translateService.getInstantTranslation(
        'GlobalApplicativeConstants.datePickerFormatHint'
      );

    this.setDisabled(this.disabled); // needed if there is no ngControl and disabled is set before initializing the input element

    this.changeDetectorRef.detectChanges();
    this.ngControl?.control?.markAsUntouched();
  }

  ngOnInit(): void {
    this.valueChangesSubscription = this.ngControl?.valueChanges?.subscribe(
      (value) => {
        this.changeDetectorRef.markForCheck();
      }
    );

    this.ngControl?.control?.addValidators([
      this.isRequired ? Validators.required : Validators.nullValidator,
    ]);
    this.changeDetectorRef.markForCheck();
  }

  writeValue(value: Date): void {
    this.value = value;
    this.changeDetectorRef.markForCheck();
  }

  registerOnKeyPress(fn: any): void {
    this.onKeyPress = fn;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.setDisabled(isDisabled);
  }

  setDisabled(newDisabled: boolean) {
    this._disabled = newDisabled;

    if (!this.ngControl && this.input?.nativeElement) {
      this.input.nativeElement.disabled = newDisabled;
    }
  }

  getInputTitle() {
    const momentVal = dayjs(this.value);
    const value = momentVal.isValid() ? momentVal.format('L') : '';
    return this.translateService.getTranslation(
      'GlobalApplicativeConstants.accessibility.inputTitle',
      {
        label: this.label,
        isRequired:
          this.hasRequiredValidator(this.ngControl?.control) &&
          !this.ngControl?.disabled &&
          !this.disabled,
        value: value,
        hasValue: !!value,
      }
    );
  }

  getAriaDescribedby() {
    const describerIdList = [];

    if (this.isError()) {
      describerIdList.push(this.errorId);
    }

    describerIdList.push(this.ariaDescribedby);

    const resultStr = describerIdList.join(' ');

    return resultStr.trim() || undefined;
  }

  getDataTestId() {
    return this.dataTestId || this.ngControl?.name || undefined;
  }

  onDateChange(event: MatDatepickerInputEvent<any, any>) {
    let newDate = dayjs(event?.value);
    if (!this.value) {
      newDate = newDate.startOf('day');
    }
    if (
      !this.getCustomDateFormatError(event?.value)?.message &&
      newDate?.isValid()
    ) {
      this.value = newDate.toDate();
      this.onChange(this.value);
      this.changeValue.emit(this.value);
    } else if (!event?.value) {
      this.value = null;
      this.onChange(this.value);
      this.changeValue.emit(this.value);
    }
  }

  hasRequiredValidator(control: AbstractControl): boolean {
    return UtilsValidators.hasRequiredValidator(control);
  }

  getFormControl() {
    return this.ngControl.control as FormControl;
  }

  displayError() {
    return !!this.getError() && this.isError();
  }

  isError() {
    return (
      this.ngControl?.invalid &&
      (this.ngControl?.dirty || this.ngControl?.touched)
    );
  }

  getCustomDateFormatError(date: string) {
    if (date?.length) {
      const momentDate = dayjs(date);
      const dateRegex = new RegExp(allowedDatePattern);
      if (
        !dateRegex.test(date) ||
        !momentDate.isValid() ||
        momentDate.isAfter(dayjs())
      ) {
        return {
          title: this.translateService.getInstantTranslation(
            'MatErrors.datePatternErrorTitle'
          ),
          message: this.translateService.getInstantTranslation(
            'MatErrors.datePatternErrorMessage'
          ),
        };
      } else {
        return null;
      }
    } else {
      return null;
    }
  }

  getError() {
    if (!this.ngControl?.control) {
      return null;
    }
    return (
      this.getCustomDateFormatError(this.input?.nativeElement?.value) ??
      this.errorMessageService.getError(this.ngControl?.control, this.label)
    );
  }

  onFocusin() {
    this.focus = true;
  }

  onFocusout() {
    this.focus = false;
  }

  isOutlineLabel() {
    return this.label && this.appearance === 'outline';
  }
}
